import { Box, BoxProps, Burger, Button, Group } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronRight } from "@tabler/icons-react";
import { Link } from "gatsby";
import React, { useEffect, useState } from "react";

import { Card, ContactUsDrawer, MobileHeaderDrawer } from "@components";

import { MenuStructure } from "@types";

import logoHorizontalBlack from "../../images/logo-horizontal-black.svg";
import logoHorizontal from "../../images/logo-horizontal.svg";
import * as classes from "./MobileHeader.module.css";

interface MobileHeaderProps extends BoxProps {
  menuStructure: MenuStructure;
  light?: boolean;
}

export function MobileHeader(props: MobileHeaderProps) {
  const { menuStructure, light = false, ...boxProps } = props;
  const [scrollY, setScrollY] = useState(0);
  const [opened, { open, close }] = useDisclosure(false);
  const [aboutOpened, { open: openAbout, close: closeAbout }] =
    useDisclosure(false);
  const [aiOpened, { open: openAi, close: closeAi }] = useDisclosure(false);
  const [servicesOpened, { open: openServices, close: closeServices }] =
    useDisclosure(false);
  const [contactUsOpened, { open: openContactUs, close: closeContactUs }] =
    useDisclosure(false);
  const scrollOpacity = Math.min(scrollY / 100, 1);

  // Find submenu data
  const aboutItem = menuStructure.items.find((item) => item.label === "About");
  const aboutSubmenu = aboutItem?.submenu;

  const aiItem = menuStructure.items.find((item) => item.label === "AI");
  const aiSubmenu = aiItem?.submenu;

  const servicesItem = menuStructure.items.find(
    (item) => item.label === "Services"
  );
  const servicesSubmenu = servicesItem?.submenu;

  // Scale down icons for mobile (24px -> 20px)
  const aboutLinks =
    aboutSubmenu?.links.map((link) => ({
      ...link,
      icon: link.icon
        ? React.cloneElement(link.icon as React.ReactElement, { size: 20 })
        : undefined,
      onClick: () => {
        closeAbout();
        close();
      },
    })) || [];

  const aiLinks =
    aiSubmenu?.links.map((link) => ({
      ...link,
      icon: link.icon
        ? React.cloneElement(link.icon as React.ReactElement, { size: 20 })
        : undefined,
      onClick: () => {
        closeAi();
        close();
      },
    })) || [];

  const serviceLinks =
    servicesSubmenu?.links.map((link) => ({
      ...link,
      icon: link.icon
        ? React.cloneElement(link.icon as React.ReactElement, { size: 20 })
        : undefined,
      onClick: () => {
        closeServices();
        close();
      },
    })) || [];

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Set initial value

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <Box {...boxProps}>
        <Card
          className={classes.container}
          p="lg"
          style={{
            backgroundColor: light
              ? `rgba(255, 255, 255, ${scrollOpacity * 1})`
              : `rgba(0, 0, 0, ${scrollOpacity * 1})`,
            transition: "background-color 0.3s ease",
            boxShadow: light
              ? `0 2px 2px 0 rgba(0, 0, 0, ${scrollOpacity * 0.05})`
              : undefined,
          }}
          c={light ? "brandBlack" : "brandWhite"}
          pos="fixed"
          top={12}
          left={12}
          right={12}
        >
          <Group justify="space-between">
            <Link to="/">
              <img
                src={light ? logoHorizontalBlack : logoHorizontal}
                alt="Airtight Design"
                width={160}
                style={{ display: "block" }}
              />
            </Link>

            <Burger
              opened={opened}
              onClick={open}
              color={light ? "black" : "white"}
              size="sm"
              lineSize={2}
            />
          </Group>
        </Card>
      </Box>

      <MobileHeaderDrawer
        opened={opened}
        onClose={close}
        title="Menu"
        overlayProps={{ backgroundOpacity: 0.9 }}
        links={menuStructure.items.map((item) => ({
          label: item.label,
          href: item.submenu ? undefined : item.href,
          onClick: item.submenu
            ? item.label === "About"
              ? openAbout
              : item.label === "AI"
                ? openAi
                : item.label === "Services"
                  ? openServices
                  : () => close()
            : () => close(),
          rightSection: item.submenu ? (
            <IconChevronRight
              size={24}
              color="var(--mantine-color-fontLightSecondary-5)"
            />
          ) : undefined,
        }))}
      >
        <Box mt="xl">
          <Button
            fullWidth
            size="sm"
            variant="glass"
            color="brandPrimary"
            leftSection={menuStructure.cta.icon}
            onClick={openContactUs}
          >
            {menuStructure.cta.label}
          </Button>
        </Box>
      </MobileHeaderDrawer>

      {/* About Sub-drawer */}
      {aboutSubmenu && (
        <MobileHeaderDrawer
          opened={aboutOpened}
          onClose={() => {
            closeAbout();
            close();
          }}
          title={aboutSubmenu.title}
          onBack={closeAbout}
          content={aboutSubmenu.content}
          cta={aboutSubmenu.cta}
          links={aboutLinks}
        />
      )}

      {/* AI Sub-drawer */}
      {aiSubmenu && (
        <MobileHeaderDrawer
          opened={aiOpened}
          onClose={() => {
            closeAi();
            close();
          }}
          title={aiSubmenu.title}
          onBack={closeAi}
          content={aiSubmenu.content}
          cta={aiSubmenu.cta}
          links={aiLinks}
        />
      )}

      {/* Services Sub-drawer */}
      {servicesSubmenu && (
        <MobileHeaderDrawer
          opened={servicesOpened}
          onClose={() => {
            closeServices();
            close();
          }}
          title={servicesSubmenu.title}
          onBack={closeServices}
          content={servicesSubmenu.content}
          cta={servicesSubmenu.cta}
          links={serviceLinks}
        />
      )}

      {/* Contact Us drawer */}
      <ContactUsDrawer
        opened={contactUsOpened}
        onClose={closeContactUs}
        onBack={closeContactUs}
        onCloseStack={() => {
          closeContactUs();
          close();
        }}
      />
    </>
  );
}
